import React, { useState, useEffect } from 'react';
import { studentsAPI } from '../services/api';
import './AlumniModal.css';

const AlumniModal = ({ alumni, agents, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    passport_number: '',
    university: '',
    program: '',
    graduation_year: '',
    application_type: '',
    package: '',
    date_of_birth: '',
    country: '',
    city: '',
    notes: '',
    reference_by: '',
    assigned_agent: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  useEffect(() => {
    if (alumni) {
      setFormData({
        first_name: alumni.first_name || '',
        last_name: alumni.last_name || '',
        email: alumni.email || '',
        phone: alumni.phone || '',
        passport_number: alumni.passport_number || '',
        university: alumni.university || '',
        program: alumni.program || '',
        graduation_year: alumni.graduation_year || '',
        application_type: alumni.application_type || '',
        package: alumni.package || '',
        date_of_birth: alumni.date_of_birth || '',
        country: alumni.country || '',
        city: alumni.city || '',
        notes: alumni.notes || '',
        reference_by: alumni.reference_by || '',
        assigned_agent: alumni.assigned_agent || '',
      });
    }
  }, [alumni]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await studentsAPI.getCountries();
        setCountries(response.data);
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Clean up form data - convert empty strings to null for optional fields
      const cleanedData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone?.trim() || null,
        passport_number: formData.passport_number?.trim().toUpperCase() || null,
        university: formData.university?.trim() || null,
        program: formData.program?.trim() || null,
        graduation_year: formData.graduation_year || null,
        application_type: formData.application_type || null,
        package: formData.package || null,
        date_of_birth: formData.date_of_birth || null,
        country: formData.country?.trim() || null,
        city: formData.city?.trim() || null,
        notes: formData.notes?.trim() || null,
        reference_by: formData.reference_by?.trim() || null,
        assigned_agent: formData.assigned_agent || null,
      };

      if (alumni) {
        await studentsAPI.updateAlumni(alumni.id, cleanedData);
      } else {
        await studentsAPI.createAlumni(cleanedData);
      }
      onSave();
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.detail || err.message || 'Error saving alumni';
      setError(errorMessage);
      console.error('Error saving alumni:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{alumni ? 'Edit Alumni' : 'Add Alumni'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>
                Passport Number
                <span style={{ marginLeft: 8, fontSize: '0.78em', color: '#888', fontWeight: 400 }}>
                  (used for mobile app login)
                </span>
              </label>
              <input
                type="text"
                name="passport_number"
                value={formData.passport_number}
                onChange={handleChange}
                placeholder="e.g. A12345678"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>University</label>
              <input
                type="text"
                name="university"
                value={formData.university}
                onChange={handleChange}
                placeholder="e.g. Istanbul University"
              />
            </div>
            <div className="form-group">
              <label>Program</label>
              <input
                type="text"
                name="program"
                value={formData.program}
                onChange={handleChange}
                placeholder="Program/Department"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Graduation Year</label>
              <input
                type="number"
                name="graduation_year"
                value={formData.graduation_year}
                onChange={handleChange}
                min="1950"
                max="2100"
                placeholder="e.g., 2024"
              />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                name="date_of_birth"
                value={formData.date_of_birth || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Application Type</label>
              <select
                name="application_type"
                value={formData.application_type}
                onChange={handleChange}
              >
                <option value="">Select application type</option>
                <option value="SCHOLARSHIP">Scholarship</option>
                <option value="SELF_FUNDED">Self-Funded</option>
                <option value="TURKISH_LANGUAGE_VISA">Turkish Language Visa</option>
              </select>
            </div>
            <div className="form-group">
              <label>Package</label>
              <select
                name="package"
                value={formData.package}
                onChange={handleChange}
              >
                <option value="">Select a package</option>
                <option value="PREMIUM">Premium Package</option>
                <option value="IN_TURKEY">In-Turkey Package</option>
                <option value="LITE">Lite Package</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Country</label>
              <select
                name="country"
                value={formData.country}
                onChange={handleChange}
                disabled={loadingCountries}
              >
                <option value="">Select a country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>
          </div>

          {agents.length > 0 && (
            <div className="form-row">
              <div className="form-group">
                <label>Assigned Agent/Employee</label>
                <select
                  name="assigned_agent"
                  value={formData.assigned_agent}
                  onChange={handleChange}
                >
                  <option value="">None</option>
                  {agents.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName || `${user.username} (${user.role === 'AGENT' ? 'Agent' : 'Employee'})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group full-width">
              <label>Reference by</label>
              <input
                type="text"
                name="reference_by"
                value={formData.reference_by}
                onChange={handleChange}
                placeholder="Person or source who referred this alumni"
              />
            </div>
          </div>

          {alumni?.referral_code && (
            <div className="form-row">
              <div className="form-group full-width">
                <label>Referral code (ambassador program)</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={alumni.referral_code}
                    readOnly
                    style={{ fontFamily: 'monospace', letterSpacing: '1px', flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(alumni.referral_code)}
                    title="Copy code"
                  >
                    Copy
                  </button>
                </div>
                <small style={{ color: '#888' }}>
                  Share this code with {alumni.first_name} — students who register with it are linked back here.
                </small>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group full-width">
              <label>Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                placeholder="Additional notes..."
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : alumni ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AlumniModal;

